"use client";

import { useState } from "react";
import { formatPhoneForDisplay } from "@/lib/phone/normalize";
import { telHref } from "@/lib/phone/normalize";
import { useLocale, useTranslations } from "next-intl";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { MapPin, Clock, Phone, Truck, Copy, Check, Store } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { Branch, ProductUsageMethod } from "@/types";
import { getDeliveryLabel } from "@/lib/utils/delivery-label";
import { copyToClipboard } from "@/lib/utils/clipboard";
import { getBranchRowAddressDisplay } from "@/lib/branches/local-address";
import BranchAddressLines from "@/components/shared/BranchAddressLines";

interface BusinessInfoModalProps {
  branches: Branch[];
  usageMethods: ProductUsageMethod[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Partner's ISO-2 country code. Used to render `delivery` as country-scoped label. */
  partnerCountryCode?: string | null;
}

const methodIcons: Record<string, React.ReactNode> = {
  pickup: <Store className="w-4 h-4" />,
  shipping: <Truck className="w-4 h-4" />,
  delivery: <Truck className="w-4 h-4" />,
};

export default function BusinessInfoModal({
  branches,
  usageMethods,
  open,
  onOpenChange,
  partnerCountryCode,
}: BusinessInfoModalProps) {
  const t = useTranslations("product");
  const tUsage = useTranslations("product.usageMethods");
  const locale = useLocale();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const getMethodLabel = (methodType: ProductUsageMethod["method_type"]) => {
    if (methodType === "delivery") {
      return getDeliveryLabel(partnerCountryCode, locale, tUsage, {
        fallbackKey: "delivery",
        templateKey: "deliveryTemplate",
      });
    }
    return t(`usageMethods.${methodType}`);
  };

  // 일본 지점(일본어 외 화면)은 현지 원문 주소를 복사한다(local-address).
  const handleCopyAddress = async (branch: Branch) => {
    const { copyText } = getBranchRowAddressDisplay(branch, locale);
    const ok = await copyToClipboard(copyText);
    if (!ok) return;
    setCopiedId(branch.id);
    toast.success(t("pricingDrawer.addressCopiedToast"));
    setTimeout(() => setCopiedId(null), 2000);
  };

  const activeMethods = usageMethods
    .filter((m) => m.enabled)
    .sort((a, b) => {
      const order: Record<string, number> = {
        pickup: 0,
        shipping: 1,
        delivery: 2,
      };
      return (order[a.method_type] ?? 99) - (order[b.method_type] ?? 99);
    });

  const dayNames = [
    t("pricingDrawer.days.sun"),
    t("pricingDrawer.days.mon"),
    t("pricingDrawer.days.tue"),
    t("pricingDrawer.days.wed"),
    t("pricingDrawer.days.thu"),
    t("pricingDrawer.days.fri"),
    t("pricingDrawer.days.sat"),
  ];

  const formatOperatingHoursWithTranslations = (hours: Branch["operating_hours"]) => {
    if (!hours || hours.length === 0) return null;

    const grouped: {
      days: number[];
      open: string;
      close: string;
      closed: boolean;
    }[] = [];

    hours
      .sort((a, b) => a.day_of_week - b.day_of_week)
      .forEach((h) => {
        const existing = grouped.find(
          (g) =>
            g.open === h.open_time &&
            g.close === h.close_time &&
            g.closed === h.is_closed
        );
        if (existing) {
          existing.days.push(h.day_of_week);
        } else {
          grouped.push({
            days: [h.day_of_week],
            open: h.open_time,
            close: h.close_time,
            closed: h.is_closed,
          });
        }
      });

    return (
      <div className="space-y-1">
        {grouped.map((g, idx) => {
          const dayStr =
            g.days.length === 7
              ? t("pricingDrawer.everyday")
              : g.days.map((d) => dayNames[d]).join(", ");
          return (
            <div key={idx}>
              {dayStr}: {g.closed ? t("pricingDrawer.closed") : `${g.open} - ${g.close}`}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("pricingDrawer.businessTab")}
      drawerHeight="tall"
      className="max-w-lg"
    >
      <div className="space-y-6">
        {branches.map((branch, index) => {
          const address = getBranchRowAddressDisplay(branch, locale);
          return (
          <div key={branch.id}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-medium">
                {index + 1}
              </div>
              <h4 className="font-semibold text-gray-900">{branch.name}</h4>
            </div>

            <div className="pl-8 space-y-2.5">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                {address.isLocal ? (
                  <BranchAddressLines
                    display={address}
                    className="flex-1 wrap-break-word"
                    primaryClassName="text-sm text-gray-700"
                  />
                ) : (
                  <span className="flex-1 text-sm text-gray-700 wrap-break-word min-w-0">
                    {branch.address}
                    {branch.address_detail && ` ${branch.address_detail}`}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleCopyAddress(branch)}
                  className={cn(
                    "flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all shrink-0",
                    copiedId === branch.id
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  )}
                >
                  {copiedId === branch.id ? (
                    <>
                      <Check className="h-3 w-3" />
                      {t("pricingDrawer.addressCopied")}
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" />
                      {address.isLocal
                        ? t("locationMap.copyLocal")
                        : t("pricingDrawer.copyAddress")}
                    </>
                  )}
                </button>
              </div>

              {branch.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 shrink-0 text-gray-400" />
                  <a
                    href={telHref(branch.phone)}
                    className="text-sm text-gray-700 hover:text-gray-900 hover:underline"
                  >
                    {formatPhoneForDisplay(branch.phone)}
                  </a>
                </div>
              )}

              {branch.operating_hours && branch.operating_hours.length > 0 && (
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                  <div className="text-sm text-gray-700">
                    {formatOperatingHoursWithTranslations(branch.operating_hours)}
                  </div>
                </div>
              )}
            </div>

            {index < branches.length - 1 && (
              <div className="border-b border-gray-100 mt-5" />
            )}
          </div>
          );
        })}

        {activeMethods.length > 0 && (
          <div className="pt-4 border-t border-gray-100">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Store className="w-4 h-4 text-orange-500" />
              {t("pricingDrawer.usageMethodsTitle")}
            </h4>
            <div className="space-y-5">
              {activeMethods.map((method, index) => (
                <div key={method.id}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs font-medium">
                      {index + 1}
                    </div>
                    <span className="font-semibold text-gray-900">
                      {getMethodLabel(method.method_type)}
                    </span>
                  </div>

                  {method.description && (
                    <div className="pl-8">
                      <div className="flex items-start gap-3">
                        {methodIcons[method.method_type] && (
                          <span className="mt-0.5 text-orange-400">
                            {methodIcons[method.method_type]}
                          </span>
                        )}
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {method.description}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
}
